"use client";
import React, { useState, useEffect, useContext, createContext } from "react";

// Create context
const AppContext = createContext();

// Provider component
export function AppProvider({ children }) {
  const [activeSection, setActiveSection] = useState('legacy');
  const [isLoading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    data: null
  });

  // Clear error after 5 seconds
  useEffect(() => {
    if (globalError) {
      const timer = setTimeout(() => {
        setGlobalError(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [globalError]);

  // Clear notifications after 3 seconds
  useEffect(() => {
    if (notifications.length > 0) {
      const timer = setTimeout(() => {
        setNotifications(prev => prev.slice(1));
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [notifications]);

  // Add notification
  const addNotification = (message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    return id;
  };

  // Remove notification
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  // Open modal
  const openModal = (type, data = null) => {
    setModalState({
      isOpen: true,
      type,
      data
    });
  };

  // Close modal
  const closeModal = () => {
    setModalState({
      isOpen: false,
      type: null,
      data: null
    });
  };

  // Start recording
  const startRecording = async (isVideo = false) => {
    try {
      const constraints = isVideo
        ? { audio: true, video: true }
        : { audio: true };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setMediaStream(stream);
      setIsRecording(true);
      return stream;
    } catch (error) {
      console.error('Error starting recording:', error);
      setGlobalError(`Failed to start recording: ${error.message}`);
      return null;
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
    setIsRecording(false);
  };

  // Context value
  const value = {
    activeSection,
    setActiveSection,
    isLoading,
    setLoading,
    globalError,
    setGlobalError,
    notifications,
    addNotification,
    removeNotification,
    modalState,
    openModal,
    closeModal,
    isRecording,
    mediaStream,
    startRecording,
    stopRecording
  };

  return (
    <AppContext.Provider value={value}>
      {/* Global loading indicator */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="cosmic-loader-inner">
            <div className="cosmic-loader-circle"></div>
            <div className="cosmic-loader-orbit"></div>
            <div className="cosmic-loader-particle"></div>
          </div>
        </div>
      )}
      
      {/* Global error message */}
      {globalError && (
        <div className="fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-pulse-slow">
          {globalError}
        </div>
      )}
      
      {/* Notifications */}
      <div className="fixed top-4 right-4 z-50 flex flex-col space-y-2">
        {notifications.map(notification => (
          <div 
            key={notification.id}
            className={`px-4 py-2 rounded-lg shadow-lg text-white ${
              notification.type === 'success' ? 'bg-green-500' :
              notification.type === 'error' ? 'bg-red-500' :
              notification.type === 'warning' ? 'bg-yellow-500' :
              'bg-blue-500'
            }`}
          >
            {notification.message}
          </div>
        ))}
      </div>
      
      {/* Modal */}
      {modalState.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="cosmic-card p-4 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {modalState.type === 'confirm' ? 'Confirm Action' :
                 modalState.type === 'info' ? 'Information' :
                 'Modal'}
              </h2>
              <button 
                onClick={closeModal}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="mb-4">
              {modalState.data?.message || 'Modal content'}
            </div>
            <div className="flex justify-end space-x-2">
              {modalState.type === 'confirm' && (
                <>
                  <button 
                    onClick={() => {
                      if (modalState.data?.onConfirm) {
                        modalState.data.onConfirm();
                      }
                      closeModal();
                    }}
                    className="cosmic-button bg-nebula-gradient"
                  >
                    Confirm
                  </button>
                  <button 
                    onClick={closeModal}
                    className="cosmic-button bg-cosmic-medium"
                  >
                    Cancel
                  </button>
                </>
              )}
              {modalState.type !== 'confirm' && (
                <button 
                  onClick={closeModal}
                  className="cosmic-button bg-nebula-gradient"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {children}
    </AppContext.Provider>
  );
}

// Custom hook to use the app context
export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
